/**
 * User Management API Service
 */

import { apiClient } from '../config';
import type {
  ManagedUser,
  UserInvite,
  UserSession,
  AuditEntry,
  AuditStats,
  InviteUserRequest,
  InviteUserResponse,
  InviteDetails,
  AcceptInviteRequest,
} from '../types';

export const UserService = {
  // Users
  async listUsers(params?: {
    page?: number;
    size?: number;
    organization_id?: string;
    status?: string;
    role?: string;
    search?: string;
  }): Promise<{ data: ManagedUser[]; total: number; page: number; size: number; pages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.size) searchParams.set('size', params.size.toString());
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.role) searchParams.set('role', params.role);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/users${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getUser(id: string): Promise<ManagedUser & {
    organizations: Array<{ id: string; name: string; slug: string; role: string }>;
    resources: Array<{ resource_type: string; resource_id: string; role: string; resource_name?: string }>;
    sessions: UserSession[];
    recent_activity: AuditEntry[];
  }> {
    const response = await apiClient.get(`/api/users/${id}`);
    return response.data.data;
  },

  async inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
    const response = await apiClient.post('/api/users', data);
    return response.data.data;
  },

  async updateUser(id: string, data: Partial<{ name: string; role: string; status: string }>): Promise<ManagedUser> {
    const response = await apiClient.put(`/api/users/${id}`, data);
    return response.data.data;
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/api/users/${id}`);
  },

  async resendInvite(userId: string): Promise<{ invite_url: string; expires_at: string }> {
    const response = await apiClient.post(`/api/users/${userId}/resend-invite`);
    return response.data.data;
  },

  // Sessions
  async getUserSessions(userId: string): Promise<UserSession[]> {
    const response = await apiClient.get(`/api/users/${userId}/sessions`);
    return response.data.data;
  },

  async terminateSession(userId: string, sessionId: string): Promise<void> {
    await apiClient.delete(`/api/users/${userId}/sessions/${sessionId}`);
  },

  async terminateAllSessions(userId: string): Promise<{ terminated_count: number }> {
    const response = await apiClient.delete(`/api/users/${userId}/sessions`);
    return response.data.data;
  },

  // Invites
  async listInvites(params?: {
    page?: number;
    size?: number;
    organization_id?: string;
  }): Promise<{ data: UserInvite[]; total: number; page: number; size: number; pages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.size) searchParams.set('size', params.size.toString());
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/invites${query ? `?${query}` : ''}`);
    return response.data;
  },

  async revokeInvite(id: string): Promise<void> {
    await apiClient.delete(`/api/invites/${id}`);
  },

  // Public invite endpoints (no auth)
  async getInviteDetails(token: string): Promise<InviteDetails> {
    const response = await apiClient.get(`/api/invites/accept/${token}`);
    return response.data.data;
  },

  async acceptInvite(token: string, data: AcceptInviteRequest): Promise<{ user: { id: string; email: string; name: string }; redirect_url: string }> {
    const response = await apiClient.post(`/api/invites/accept/${token}`, data);
    return response.data.data;
  },

  // Audit
  async listAuditEntries(params?: {
    page?: number;
    size?: number;
    organization_id?: string;
    user_id?: string;
    action?: string;
    resource_type?: string;
    resource_id?: string;
    from?: string;
    to?: string;
  }): Promise<{ data: AuditEntry[]; total: number; page: number; size: number; pages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.size) searchParams.set('size', params.size.toString());
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params?.user_id) searchParams.set('user_id', params.user_id);
    if (params?.action) searchParams.set('action', params.action);
    if (params?.resource_type) searchParams.set('resource_type', params.resource_type);
    if (params?.resource_id) searchParams.set('resource_id', params.resource_id);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/audit${query ? `?${query}` : ''}`);
    return response.data;
  },

  async getAuditActions(): Promise<string[]> {
    const response = await apiClient.get('/api/audit/actions');
    return response.data.data;
  },

  async getAuditStats(params?: {
    organization_id?: string;
    days?: number;
  }): Promise<AuditStats> {
    const searchParams = new URLSearchParams();
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params?.days) searchParams.set('days', params.days.toString());

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/audit/stats${query ? `?${query}` : ''}`);
    return response.data.data;
  },

  async exportAuditLog(params?: {
    format?: 'json' | 'csv';
    organization_id?: string;
    user_id?: string;
    action?: string;
    from?: string;
    to?: string;
  }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (params?.format) searchParams.set('format', params.format);
    if (params?.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params?.user_id) searchParams.set('user_id', params.user_id);
    if (params?.action) searchParams.set('action', params.action);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);

    const query = searchParams.toString();
    const response = await apiClient.get(`/api/audit/export${query ? `?${query}` : ''}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // User Roles
  async getUserRoles(userId: string): Promise<{
    assignments: Array<{
      id: string;
      role_id: string;
      role_name: string;
      role_scope: string;
      organization_id?: string;
      organization_name?: string;
      resource_type?: string;
      resource_id?: string;
      assigned_by?: string;
      assigned_by_name?: string;
      expires_at?: string;
      created_at: string;
    }>;
    effective_roles: Array<{
      role_id: string;
      role_name: string;
      scope: string;
      priority: number;
      is_system: boolean;
    }>;
  }> {
    const response = await apiClient.get(`/api/users/${userId}/roles`);
    return response.data.data;
  },

  async assignUserRole(userId: string, data: {
    role_id: string;
    organization_id?: string;
    resource_type?: string;
    resource_id?: string;
    expires_at?: string;
  }): Promise<{ id: string }> {
    const response = await apiClient.post(`/api/users/${userId}/roles`, data);
    return response.data.data;
  },

  async removeUserRole(userId: string, assignmentId: string): Promise<void> {
    await apiClient.delete(`/api/users/${userId}/roles/${assignmentId}`);
  },

  async getEffectivePermissions(userId: string): Promise<{
    permissions: Array<{
      permission_id: string;
      permission_name: string;
      resource: string;
      action: string;
      granted: boolean;
      source_role_id: string;
      source_role_name: string;
    }>;
  }> {
    const response = await apiClient.get(`/api/users/${userId}/effective-permissions`);
    return response.data.data;
  },
};
